import { Children, cloneElement, isValidElement, ReactElement, useMemo } from 'react';
import { isDropdownItemElement } from '../../DropdownItem/utils';
import { ChildProps } from '../types';
import { DropdownGroupLabelProps } from '../DropdownGroupLabel';

interface UseMenuItemsProps {
    children: React.ReactNode;
    itemsRef: React.MutableRefObject<(HTMLDivElement | null)[]>;
    close?: () => void;
    activeItemIndex?: number;
}

export const DROPDOWN_ITEM_ROLE = 'menuitem';

const isDropdownGroupLabel = (element: ReactElement): element is ReactElement<DropdownGroupLabelProps> => {
    return typeof element.type !== 'string' && 'groupId' in element.props;
};

export const useMenuItems = ({ children, itemsRef, close, activeItemIndex }: UseMenuItemsProps) =>
    useMemo(() => {
        let currentGroupId: string | undefined;

        return Children.map(children, (c, index) => {
            if (!isValidElement<ChildProps>(c)) {
                return c;
            }

            if (isDropdownGroupLabel(c)) {
                currentGroupId = c.props.groupId;
            }

            if (isDropdownItemElement(c)) {
                const handleItemClick = (event: React.SyntheticEvent, value?: unknown) => {
                    if (c.props.onClick) {
                        (c.props.onClick as (event: React.SyntheticEvent, value?: unknown) => void)(event, value);
                    }
                    close?.();
                };

                const handleItemRef = (element: HTMLDivElement) => {
                    itemsRef.current[index] = element;
                };

                const handleMouseOver = () => {
                    const item = itemsRef.current[index];
                    if (item) {
                        item.focus();
                    }
                };

                return cloneElement(c, {
                    onClick: handleItemClick,
                    onMouseOver: handleMouseOver,
                    innerRef: handleItemRef,
                    tabIndex: index === activeItemIndex ? 0 : -1,
                    role: DROPDOWN_ITEM_ROLE,
                    'aria-selected': index === activeItemIndex,
                    'aria-describedby': currentGroupId ? `${currentGroupId}-label` : undefined,
                });
            }

            // Return other components (like Divider) as is
            return c;
        });
    }, [activeItemIndex, children, close, itemsRef]);
